import { useEffect, useMemo, useRef, useState } from 'react';
import './style.scss';
import SelectFormGroup from '../../Shared/SelectFormGroup';
import SelectInputGroup from '../../Shared/SelectInputGroup';
import ValidationInfo from '../../../validation/validation-info';
import ScoreBoardEntryApiModel from '../models/scoreboard-entry.api-model';
import useFetch, { Status } from '../../../hooks/fetch.hook';
import Spinner from '../../Shared/Spinner';
import type IScoreboardEntry from '../../../models/pa-egna-ben/scoreboard-entry.model';
import ScoreboardMunicipalityApiModel from '../models/scoreboard-municipality.api-model';
import type IScoreboardMunicipality from '../../../models/pa-egna-ben/scoreboard-municipality.model';
import { MunicipalitySelectorModalHelper } from '../../../../components/municipality-selector-modal/municipality-selector-modal.helper';
import whoseLegsWins from '../../../../assets/illustrations/whose-legs-lead.svg';
import DataUrlHelper from '../../../helpers/data-url.helper';
import { AnalyticsService } from '../../../services/analytics-service';
import { IServerSideRendered } from '../../Shared/Base/server-side-rendered';
import { Icon } from '../../Shared/Icon';
import { IconMotif } from '../../Shared/Icon/icon-motif.enum';
import ScoreboardHelper from '../helpers/scoreboard.helper';
import { SanitizerService } from '../../../services/sanitizer.service';

export interface IScoreboardTexts {
  textBeforeCompetitionIsOpen: string;
  textBeforeStartOfFirstLeg: string;
  textWhenScoreboardIsHidden: string;
  linkBelowTableText: string;
  description: string;
}

export interface IScoreboardSettings {
  numberOfParticipations: number;
  isVisible: boolean;
  hiddenReason: string;
  useLayoutWithBorderAndButton: boolean;
  linkBelowTableUrl: string;
}

type IScoreboardProps = IServerSideRendered<IScoreboardTexts, IScoreboardSettings>;

enum ScoreboardHiddenReason {
  None,
  CompetitionIsNotOpen,
  Leg1HasNotStarted,
  ScoreboardIsHidden,
  NoCompetition,
}

function processRecievedScoreboardData(data: ScoreBoardEntryApiModel[]): IScoreboardEntry[] {
  return data ? data.map(ScoreBoardEntryApiModel.mapToBusinessModel) : [];
}

function processRecievedMunicipalityData(data: ScoreboardMunicipalityApiModel[]): IScoreboardMunicipality[] {
  return data ? data.map(ScoreboardMunicipalityApiModel.mapToBusinessModel) : [];
}

function normalizeNumberOfParticipations(numberOfParticipations: number): number {
  return Number.isNaN(numberOfParticipations) || numberOfParticipations <= -1 ? Number.MAX_SAFE_INTEGER : numberOfParticipations;
}

function Scoreboard({ settings, texts }: IScoreboardProps) {
  // === CONSTANTS ===
  const hiddenReason = ScoreboardHiddenReason[settings.hiddenReason as keyof typeof ScoreboardHiddenReason] || ScoreboardHiddenReason.None;
  const numberOfParticipations = normalizeNumberOfParticipations(settings.numberOfParticipations);

  // === REFS ===
  const inputRef = useRef<HTMLInputElement>(null);

  // === STATES ===
  const [municipalitySelectedInDropdown, setMunicipalitySelectedInDropdown] = useState<string>('');
  const [selectedMunicipality, setSelectedMunicipality] = useState<string>('');
  const [searchText, setSearchText] = useState<string>('');
  const [scoreboardData, setScoreboardData] = useState<ScoreBoardEntryApiModel[]>([]);
  const [expandedScoreBoardSearch, setExpandedScoreBoardSearch] = useState<boolean>(false);
  const [municipalityQueryParameter, setMunicipalityQueryParameter] = useState<string>('');

  // === FETCH DATA ===
  const [getMunicipalityScoreboardResult] = useFetch<ScoreBoardEntryApiModel[]>(selectedMunicipality ? `/api/paegnaben/scoreboard/${selectedMunicipality}` : '/api/paegnaben/scoreboard');
  const [getParticipatingMunicipalitiesResult] = useFetch<ScoreboardMunicipalityApiModel[]>('/api/paegnaben/scoreboard/participating-municipalities');

  useEffect(() => {
    function filterScoreboardData(data: ScoreBoardEntryApiModel[], text: string): IScoreboardEntry[] {
      if (!data) {
        return [];
      }
      if (!text) {
        return processRecievedScoreboardData(data).slice(0, numberOfParticipations);
      }
      const lowerCaseSearchText = text.toLowerCase().trim();
      const filteredData = data.filter((entry) => entry.participationName.toLowerCase().includes(lowerCaseSearchText) || entry.schoolName.toLowerCase().includes(lowerCaseSearchText));
      return processRecievedScoreboardData(filteredData);
    }

    if (getMunicipalityScoreboardResult.status === Status.success && getMunicipalityScoreboardResult.data) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setScoreboardData(filterScoreboardData(getMunicipalityScoreboardResult.data, searchText));
    }
  }, [getMunicipalityScoreboardResult, numberOfParticipations, searchText]);

  // == EFFECTS ===
  useEffect(() => {
    const municipalityFromCookies = MunicipalitySelectorModalHelper.getSelectedMunicipalityFromCookies();
    const municipalityFromUrl = MunicipalitySelectorModalHelper.getSelectedMunicipalityFromUrl();

    if (municipalityFromUrl) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMunicipalitySelectedInDropdown(municipalityFromUrl);
      setSelectedMunicipality(municipalityFromUrl);
    } else if (municipalityFromCookies && municipalityFromCookies.gid !== MunicipalitySelectorModalHelper.NO_SELECTED_MUNICIPALITY_GID) {
      setMunicipalitySelectedInDropdown(municipalityFromCookies.gid);
      setSelectedMunicipality(municipalityFromCookies.gid);
    }
  }, []);

  // === VARIABLES ===
  const participatingMunicipalities = getParticipatingMunicipalitiesResult.data ? processRecievedMunicipalityData(getParticipatingMunicipalitiesResult.data) : undefined;
  const linkUrl = settings.linkBelowTableUrl;
  const linkText = texts.linkBelowTableText;
  const sanitizedDescriptionHtml = useMemo(() => SanitizerService.sanitizeHtml(texts.description), [texts.description]);
  const scoreboardStatistics = getMunicipalityScoreboardResult.status === Status.success ? ScoreboardHelper.calculateStats(getMunicipalityScoreboardResult.data) : null;

  // === HELPERS ===
  function renderScoreboardStatistics() {
    if (!scoreboardStatistics) return null;
    const statItemClassName = `d-flex align-items-center border rounded-pill p-2 scoreboard__stat-item gap-1 ${
      settings.useLayoutWithBorderAndButton ? 'scoreboard__stat-item--layout-with-border-and-button' : ''
    }`.trim();

    return (
      <div className="mb-3">
        <div className="d-flex flex-wrap gap-2">
          <div className={statItemClassName}>
            <div className="icon-wrapper">
              <Icon motif={IconMotif.FaceLaugh} altText=""></Icon>
            </div>
            <span>{scoreboardStatistics.totalStudents} elever</span>
          </div>
          <div className={statItemClassName}>
            <div className="icon-wrapper">
              <Icon motif={IconMotif.ShoeWalking} altText=""></Icon>
            </div>
            <span>{scoreboardStatistics.totalKilometers} km</span>
          </div>
          <div className={statItemClassName}>
            <div className="icon-wrapper">
              <Icon motif={IconMotif.Leaf} altText=""></Icon>
            </div>
            <span>{scoreboardStatistics.totalCarbonDioxide} kg koldioxid sparat</span>
          </div>
        </div>
      </div>
    );
  }

  function getCurrentMunicipalityName(municipalityGid: string): string {
    return participatingMunicipalities?.find((m) => m.gid === municipalityGid)?.name ?? '';
  }

  // === EVENT HANDLERS ===
  function handleInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    const newSearchText = event.target.value.trimStart();
    setSearchText(newSearchText);
  }

  function handleExpandScoreBoardSearch() {
    if (!expandedScoreBoardSearch) {
      setExpandedScoreBoardSearch(!expandedScoreBoardSearch);
      AnalyticsService.addEvent(AnalyticsService.PaEgnaBenPublic, 'Topplistan klick på expandera sök desktop');
      // NV-6548: Wait 100 ms so that it input has the time to be displayed before focusing
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }

  function handleContractScoreBoardSearch() {
    // if window size is less than 576px we only want to clear filter in put text
    if (window.innerWidth >= 576) {
      setExpandedScoreBoardSearch(false);
    }
    setSearchText('');
    inputRef.current?.focus();
  }

  function combineUrlWithMunicipalityQueryParameter(baseUrl: string, queryParameter?: string): string {
    if (queryParameter !== '') {
      const sanitizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      return `${sanitizedBaseUrl}?m=${queryParameter}`;
    }
    return baseUrl;
  }

  function removeMunicipalityQueryParameter() {
    const newUrl = window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }

  return (
    <div className={`scoreboard ${settings.useLayoutWithBorderAndButton ? 'border border-blue border-bottom-wide' : ''}`.trimEnd()}>
      <>
        {!settings.useLayoutWithBorderAndButton && hiddenReason !== ScoreboardHiddenReason.ScoreboardIsHidden && (
          <div className="mb-3">
            <div className="row">
              <div className="col-12 col-sm-6">
                {participatingMunicipalities && (
                  <SelectFormGroup
                    id="scoreboard-municipality-selector"
                    className="mb-3 mb-sm-0"
                    labelText="Välj kommun"
                    hideLabel={true}
                    options={[
                      {
                        value: '',
                        text: `Alla kommuner (${participatingMunicipalities.reduce((x, c) => x + c.numberOfParticipations, 0)})`,
                        ariaLabel: `Alla kommuner (${participatingMunicipalities.reduce((x, c) => x + c.numberOfParticipations, 0)} klasser)`,
                      },
                      ...participatingMunicipalities.map((m) => ({ value: m.gid, text: `${m.name} (${m.numberOfParticipations})`, ariaLabel: `${m.name} (${m.numberOfParticipations} klasser)` })),
                    ]}
                    state={{ value: municipalitySelectedInDropdown, validationInfo: new ValidationInfo() }}
                    onChange={(x) => {
                      setMunicipalitySelectedInDropdown(x);
                      setSelectedMunicipality(x);
                      setMunicipalityQueryParameter(x);
                      const selectedOption = participatingMunicipalities.find((m) => m.gid === x);
                      const optionText = selectedOption ? selectedOption.name : 'Alla kommuner';
                      AnalyticsService.addEvent(AnalyticsService.PaEgnaBenPublic, 'Topplistan klick på `Byt kommun`', optionText);
                    }}
                  />
                )}
              </div>
              {/* Stats should only be visible here on mobile view */}
              <div className="col-12 d-sm-none">{settings.isVisible && renderScoreboardStatistics()}</div>
              <div className="col-12 col-sm-6">
                <div className={`score-board-search-wrapper${expandedScoreBoardSearch ? '--expanded' : ''} d-flex align-items-center rounded-2 border border-gray-700 search-filter`}>
                  <button type="button" className="btn--search" aria-label="Sök" onClick={handleExpandScoreBoardSearch}></button>
                  <div className={`flex-grow-1 score-board-search__input ${expandedScoreBoardSearch ? 'score-board-search__input--expanded' : ''}`}>
                    <label htmlFor="score-board-search-input" className="visually-hidden">
                      Sök på skola eller klass
                    </label>
                    <input
                      id="score-board-search-input"
                      type="text"
                      className="form-control border border-0 shadow-none ps-0"
                      placeholder="Skola eller klass"
                      value={searchText}
                      onChange={handleInputChange}
                      ref={inputRef}
                    />
                  </div>
                  <button
                    type="button"
                    className={`score-board-search__contract-clear ${expandedScoreBoardSearch ? 'score-board-search__contract-clear--expanded' : ''} btn-close mx-1`}
                    aria-label="Stäng"
                    onClick={handleContractScoreBoardSearch}
                  ></button>
                </div>
              </div>
            </div>
          </div>
        )}

        {settings.useLayoutWithBorderAndButton && participatingMunicipalities && (
          <div className="bg-blue border-bottom border-blue p-3 p-sm-4">
            <SelectInputGroup
              id="scoreboard-municipality-selector"
              className="mb-0"
              labelText="Välj din kommun"
              options={[
                {
                  value: '',
                  text: `Alla kommuner (${participatingMunicipalities.reduce((x, c) => x + c.numberOfParticipations, 0)})`,
                  ariaLabel: `Alla kommuner (${participatingMunicipalities.reduce((x, c) => x + c.numberOfParticipations, 0)} klasser)`,
                },
                ...participatingMunicipalities.map((m) => ({ value: m.gid, text: `${m.name} (${m.numberOfParticipations})`, ariaLabel: `${m.name} (${m.numberOfParticipations} klasser)` })),
              ]}
              state={{ value: municipalitySelectedInDropdown, validationInfo: new ValidationInfo() }}
              onChange={setMunicipalitySelectedInDropdown}
              onSelect={(x) => {
                setSelectedMunicipality(x);
                removeMunicipalityQueryParameter();
              }}
            />
          </div>
        )}

        <div className={settings.useLayoutWithBorderAndButton ? 'p-3 p-sm-4' : ''}>
          {!settings.isVisible && (hiddenReason === ScoreboardHiddenReason.CompetitionIsNotOpen || hiddenReason === ScoreboardHiddenReason.NoCompetition) && (
            <div className="last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: texts.textBeforeCompetitionIsOpen }}></div>
          )}
          {!settings.isVisible && hiddenReason === ScoreboardHiddenReason.Leg1HasNotStarted && (
            <div className="last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: texts.textBeforeStartOfFirstLeg }}></div>
          )}
          {!settings.isVisible && hiddenReason === ScoreboardHiddenReason.ScoreboardIsHidden && (
            <>
              {settings.useLayoutWithBorderAndButton && <div className="last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: texts.textWhenScoreboardIsHidden }}></div>}
              {!settings.useLayoutWithBorderAndButton && (
                <div className="p-3 bg-blue">
                  <div className="row justify-content-center">
                    <img src={DataUrlHelper.encode(whoseLegsWins)} alt="" className="mb-3 col-12 col-md-6" />
                  </div>
                  <div className="row justify-content-center">
                    <div className="col-12 col-md-8 text-center" dangerouslySetInnerHTML={{ __html: texts.textWhenScoreboardIsHidden }}></div>
                  </div>
                </div>
              )}
            </>
          )}
          {settings.isVisible && getMunicipalityScoreboardResult.status === Status.loading && <Spinner />}
          {settings.isVisible && getMunicipalityScoreboardResult.status === Status.failed && <p>Det gick inte att hämta ställningen i tävlingen.</p>}
          {/* Stats should only be visible here on desktop view, but on all sizes when useLayoutWithBorderAndButton */}
          {settings.isVisible && <div className={settings.useLayoutWithBorderAndButton ? '' : 'd-none d-sm-block'}>{renderScoreboardStatistics()}</div>}
          {settings.isVisible &&
            getMunicipalityScoreboardResult.status === Status.success &&
            scoreboardData &&
            (scoreboardData.length === 0 ? (
              <span>Ingen skola eller klass hittades</span>
            ) : (
              <>
                <table className="table table--last-row-no-border scoreboard-table mb-0">
                  <caption className="visually-hidden">Topplista</caption>
                  <thead>
                    <tr>
                      <th scope="col" className="scoreboard__column--position">
                        <span className="visually-hidden">Position</span>
                      </th>
                      <th scope="col" className="scoreboard__column--name">
                        <span className="visually-hidden">Namn</span>
                      </th>
                      <th scope="col" className="scoreboard__column--municipality d-none d-sm-table-cell">
                        <span className="visually-hidden">Kommun</span>
                      </th>
                      <th scope="col" className="scoreboard__column--score">
                        <span className="visually-hidden">Poäng</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {scoreboardData.map((scoreboardResult) => (
                      <tr className="" key={scoreboardResult.rank}>
                        <td className="scoreboard__column--position">
                          <div
                            className={`text-nowrap text-center scoreboard__position ${
                              scoreboardResult.rank <= 3 ? `scoreboard__position--star scoreboard__position--star-${scoreboardResult.rank} fw-bold` : ''
                            }`}
                          >
                            {scoreboardResult.rank}
                          </div>
                        </td>
                        <td className={`scoreboard__column--name text-break ${scoreboardResult.rank <= 3 ? 'fw-bold' : ''}`}>
                          <a href={`/klass/${scoreboardResult.participationId}`}>
                            {scoreboardResult.participationName} {scoreboardResult.schoolName}
                          </a>
                        </td>
                        <td className="scoreboard__column--municipality d-none d-sm-table-cell">{getCurrentMunicipalityName(scoreboardResult.municipalityGid)}</td>
                        <td className="scoreboard__column--score text-nowrap text-end">
                          <span className="d-md-none" aria-hidden>
                            {scoreboardResult.weightedTotalScore} <abbr title="poäng">p</abbr>
                          </span>
                          <span className="d-md-none visually-hidden">{scoreboardResult.weightedTotalScore} poäng</span>
                          <span className="d-none d-md-block">{scoreboardResult.weightedTotalScore} poäng</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {texts.description && <div className="mt-2 last-child-margin-bottom-none" dangerouslySetInnerHTML={{ __html: sanitizedDescriptionHtml }}></div>}
                {linkText && linkUrl && (
                  <a href={combineUrlWithMunicipalityQueryParameter(linkUrl, municipalityQueryParameter)} className="float-end mt-2">
                    {linkText}
                  </a>
                )}
              </>
            ))}
        </div>
      </>
    </div>
  );
}

export default Scoreboard;
